import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { isBrowser } from '@utils'; // Assuming you have this utility, adjust path if necessary
import { logClubEvent } from '@utils'; // Add logging import for analytics

// Interface for authentication data
export interface AuthData {
    token: string;
    email: string;
}

// Interface for the context value
interface IAuthContext {
    authData: AuthData | null;
    isAuthenticated: boolean;
    isLoadingAuth: boolean; // To track initial auth status loading
    login: (token: string, email: string) => void;
    logout: () => void;
    updateAuthEmail: (newEmail: string) => void;
}

// Create the context with an undefined initial value
const AuthContext = createContext<IAuthContext | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [authData, setAuthData] = useState<AuthData | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true); // Start as true

    useEffect(() => {
        // Check initial auth status from localStorage only on the client side
        if (isBrowser()) {
            try {
                const token = localStorage.getItem('userToken');
                const email = localStorage.getItem('userEmail');
                if (token && email) {
                    setAuthData({ token, email });
                    setIsAuthenticated(true);
                }
            } catch (error) {
                console.error("Error accessing localStorage:", error);
                // Handle environments where localStorage might be blocked or unavailable
            } finally {
                setIsLoadingAuth(false); // Set to false after attempting to load
            }
        } else {
            setIsLoadingAuth(false); // Not in browser, no auth to load from localStorage
        }
    }, []);

    const login = (token: string, email: string) => {
        if (isBrowser()) {
            try {
                localStorage.setItem('userToken', token);
                localStorage.setItem('userEmail', email);
            } catch (error) {
                console.error("Error accessing localStorage:", error);
            }
        }
        const newAuthData = { token, email };
        setAuthData(newAuthData);
        setIsAuthenticated(true);
    };

    const logout = () => {
        logClubEvent('web_logout_initiated', {
            timestamp: new Date().toISOString()
        });

        if (isBrowser()) {
            try {
                // Existing authentication cleanup
                localStorage.removeItem('userToken');
                localStorage.removeItem('userEmail');
                // Remove AI generated workout data
                localStorage.removeItem('saved-generated-workout');

                // Enhanced cleanup: Remove user bookmarks and preferences
                localStorage.removeItem('bookmarkedWorkouts');
                localStorage.removeItem('bookmarkedPlans');

                // Enhanced cleanup: Remove session storage data
                sessionStorage.removeItem('pendingTeamJoin');

                logClubEvent('web_logout_cleanup_success', {
                    cleaned_localStorage: ['userToken', 'userEmail', 'saved-generated-workout', 'bookmarkedWorkouts', 'bookmarkedPlans'],
                    cleaned_sessionStorage: ['pendingTeamJoin'],
                    timestamp: new Date().toISOString()
                });
            } catch (error) {
                console.error("Error accessing storage during logout:", error);
                logClubEvent('web_logout_cleanup_error', {
                    error: error instanceof Error ? error.message : 'Unknown error',
                    timestamp: new Date().toISOString()
                });
            }
        }

        setAuthData(null);
        setIsAuthenticated(false);

        logClubEvent('web_logout_completed', {
            timestamp: new Date().toISOString()
        });
    };

    const updateAuthEmail = (newEmail: string) => {
        if (isBrowser() && authData) { // Ensure authData exists before trying to update its email
            try {
                localStorage.setItem('userEmail', newEmail);
            } catch (error) {
                console.error("Error accessing localStorage:", error);
            }
            setAuthData(prevAuthData => ({ ...prevAuthData!, email: newEmail }));
        }
    };

    return (
        <AuthContext.Provider value={{ authData, isAuthenticated, isLoadingAuth, login, logout, updateAuthEmail }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the AuthContext
export const useAuth = (): IAuthContext => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 